import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useState,
} from 'react';

import { type Theme, useTheme } from '@dr.pogodin/react-utils';

import defaultTheme from 'themes/AppThrobber/default.scss';

const THROBBER_DISPLAY_DELAY = 300; // [ms]

type ThemeT = Theme<'blocking' | 'loader' | 'loaderContainer' | 'overlay'
  | 'progress' | 'wrapper'>;

type PropsT = {
  /** Opts for the overlay to block pointer events. Requires withOverlay
   *  to also be opted in. */
  blockPointerEvents?: boolean;

  /** Progress percentage (a number within [0, 100] range) to display
   *  in the throbber. */
  progress?: number;

  /** Delay initial rendering of the throbber into DOM by
   *  THROBBER_DISPLAY_DELAY, to avoid a short throbber blink
   *  by rapid async operations. */
  withDebounce?: boolean;

  theme?: ThemeT;

  /** Adds an absolutely-positioned semi-transparent overlay over the content
   *  and renders the throbber in the center of it. */
  withOverlay?: boolean;
};

const Throbber: FunctionComponent<PropsT> = ({
  blockPointerEvents,
  progress,
  theme,
  withDebounce,
  withOverlay,
}) => {
  const composed = useTheme('AppThrobber', defaultTheme, theme);

  const [show, setShow] = useState(!withDebounce);

  useEffect(() => {
    let id: NodeJS.Timeout;
    if (withDebounce) {
      id = setTimeout(() => {
        setShow(true);
      }, THROBBER_DISPLAY_DELAY);
    }
    return () => {
      clearTimeout(id);
    };
  }, [withDebounce]);

  let throbber: Exclude<ReactNode, Promise<unknown>>;

  if (show) {
    throbber = (
      // NOTE: The "loader container" is necessary to solve this problem:
      // when this throbber is placed at an edge of a scrolling container,
      // and it rotates, the rotated loader square is evaluated as crossing
      // beyond the scrolling viewport, thus continiously interfering with
      // the scrolling state in UI. The "loader container" has "overflow: hidden"
      // thus it just hides that change of dimension when rotating from
      // the outer elements.
      <div className={composed.loaderContainer}>
        <div className={composed.loader} />
      </div>
    );
    if (withOverlay) {
      let className = composed.overlay ?? '';
      if (blockPointerEvents) className += ` ${composed.blocking ?? ''}`;
      throbber = <div className={className}>{throbber}</div>;
    }

    if (typeof progress === 'number') {
      throbber = (
        <div className={composed.wrapper}>
          {throbber}
          <div className={composed.progress}>{progress}%</div>
        </div>
      );
    }
  }

  return throbber;
};

export default Throbber;
